// frontend/src/pages/technician/TechnicianLogin.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Fade
} from '@mui/material';
import {
    Build,
    Visibility,
    VisibilityOff,
    Email as EmailIcon,
    Lock as LockIcon
} from '@mui/icons-material';
import { useTechnicianAuth } from '../../contexts/TechnicianAuthContext';

const TechnicianLogin = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [companySettings, setCompanySettings] = useState({
        company_name: 'YinoWISP',
        company_logo: ''
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { login, technicianToken, isAuthenticated } = useTechnicianAuth();

    // Redirigir si ya está autenticado
    useEffect(() => {
        if (technicianToken && isAuthenticated()) {
            const from = location.state?.from?.pathname || '/technician/dashboard';
            navigate(from, { replace: true });
        }
    }, [technicianToken, isAuthenticated, navigate, location]);

    // Cargar configuraciones públicas de la empresa (Nombre y Logo)
    useEffect(() => {
        const fetchCompanySettings = async () => {
            try {
                const response = await fetch('/api/public/company-settings');
                if (response.ok) {
                    const data = await response.json();
                    setCompanySettings({
                        company_name: data.company_name || 'YinoWISP',
                        company_logo: data.company_logo || ''
                    });
                }
            } catch (err) {
                console.error('Error fetching company settings for tech login:', err);
            }
        };
        fetchCompanySettings();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.email || !formData.password) {
            setError('Por favor, completa todos los campos');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const result = await login(formData.email, formData.password);

            if (result.success) {
                const from = location.state?.from?.pathname || '/technician/dashboard';
                navigate(from, { replace: true });
            } else {
                setError(result.error || 'Error de autenticación. Verifica tus credenciales.');
            }
        } catch (err) {
            console.error('Error en login:', err);
            setError('Error de autenticación. Verifica tus credenciales.');
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                background: 'radial-gradient(circle at center, #0a0f24 0%, #02040a 100%)',
                py: 4,
                px: 2,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '380px',
                    height: '380px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, rgba(99, 102, 241, 0) 70%)',
                    top: '10%',
                    left: '10%',
                    filter: 'blur(60px)',
                    zIndex: 1
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: '380px',
                    height: '380px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.08) 0%, rgba(168, 85, 247, 0) 70%)',
                    bottom: '10%',
                    right: '10%',
                    filter: 'blur(60px)',
                    zIndex: 1
                }
            }}
        >
            <Fade in={true} timeout={800}>
                <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 10 }}>
                    <Card
                        elevation={0}
                        sx={{
                            background: 'rgba(11, 15, 30, 0.8)',
                            backdropFilter: 'blur(30px)',
                            border: '1px solid rgba(255, 255, 255, 0.06)',
                            borderRadius: '24px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                            }
                        }}
                    >
                        <CardContent sx={{ p: { xs: 4, sm: 5 } }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                {companySettings.company_logo ? (
                                    <Box
                                        component="img"
                                        src={companySettings.company_logo}
                                        alt={companySettings.company_name}
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            objectFit: 'contain',
                                            mb: 2.5,
                                            mx: 'auto',
                                            display: 'block',
                                            borderRadius: '24px',
                                            filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.35))',
                                            '@keyframes floatAndGlow': {
                                                '0%, 100%': { transform: 'translateY(0px) scale(1)', filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.35))' },
                                                '50%': { transform: 'translateY(-6px) scale(1.02)', filter: 'drop-shadow(0 0 25px rgba(168, 85, 247, 0.55))' },
                                            },
                                            animation: 'floatAndGlow 4s ease-in-out infinite'
                                        }}
                                    />
                                ) : (
                                    <Box 
                                        sx={{ 
                                            width: '64px', 
                                            height: '64px', 
                                            borderRadius: '20px', 
                                            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                                            border: '2px solid rgba(99, 102, 241, 0.35)',
                                            boxShadow: '0 0 20px rgba(99, 102, 241, 0.15)',
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            margin: '0 auto 20px auto'
                                        }}
                                    >
                                        <Build sx={{ fontSize: '32px', color: '#6366f1' }} />
                                    </Box>
                                )}

                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        fontFamily: '"Outfit", "Inter", sans-serif',
                                        fontWeight: 900,
                                        background: 'linear-gradient(90deg, #38bdf8 0%, #c084fc 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        letterSpacing: '-0.5px',
                                        mb: 0.5
                                    }}
                                >
                                    YinoWISP
                                </Typography>

                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: 'rgba(255, 255, 255, 0.5)', 
                                        fontFamily: '"Outfit", "Inter", sans-serif', 
                                        fontWeight: 600, 
                                        fontSize: '0.85rem', 
                                        letterSpacing: '0.5px', 
                                        textTransform: 'uppercase', 
                                        mt: 1 
                                    }}
                                >
                                    Portal del Técnico
                                </Typography>
                            </Box>

                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mb: 3,
                                        borderRadius: '12px',
                                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                                        color: '#f87171',
                                        border: '1px solid rgba(239, 68, 68, 0.2)',
                                        '& .MuiAlert-icon': { color: '#f87171' },
                                        fontFamily: "'Outfit', sans-serif"
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <TextField
                                    fullWidth
                                    label="Correo Electrónico"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    autoFocus
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                    variant="outlined"
                                    InputLabelProps={{
                                        sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#38bdf8' }, fontFamily: "'Outfit', sans-serif" }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            color: '#ffffff',
                                            bgcolor: '#080c1a',
                                            borderRadius: '12px',
                                            fontFamily: "'Outfit', sans-serif",
                                            transition: 'all 0.3s ease',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255, 255, 255, 0.08)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#38bdf8',
                                                boxShadow: '0 0 12px rgba(56, 189, 248, 0.15)'
                                            },
                                            '& input': {
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 100px #080c1a inset !important',
                                                    WebkitTextFillColor: '#ffffff !important',
                                                    transition: 'background-color 5000s ease-in-out 0s',
                                                }
                                            }
                                        }
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    name="password"
                                    label="Contraseña"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                    variant="outlined"
                                    InputLabelProps={{
                                        sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#c084fc' }, fontFamily: "'Outfit', sans-serif" }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ color: '#c084fc', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleTogglePassword}
                                                    edge="end"
                                                    disabled={loading}
                                                    sx={{
                                                        color: 'rgba(255, 255, 255, 0.45)',
                                                        '&:hover': { color: '#c084fc' }
                                                    }}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            color: '#ffffff',
                                            bgcolor: '#080c1a',
                                            borderRadius: '12px',
                                            fontFamily: "'Outfit', sans-serif",
                                            transition: 'all 0.3s ease',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255, 255, 255, 0.08)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#c084fc',
                                                boxShadow: '0 0 12px rgba(192, 132, 252, 0.15)'
                                            },
                                            '& input': {
                                                '&:-webkit-autofill': {
                                                    WebkitBoxShadow: '0 0 0 100px #080c1a inset !important',
                                                    WebkitTextFillColor: '#ffffff !important',
                                                    transition: 'background-color 5000s ease-in-out 0s',
                                                }
                                            }
                                        }
                                    }}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        mt: 2,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '15px',
                                        py: 1.5,
                                        fontFamily: "'Outfit', sans-serif",
                                        background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                                        boxShadow: '0 4px 20px rgba(192, 132, 252, 0.3)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 24px rgba(192, 132, 252, 0.45)',
                                        },
                                        '&:active': {
                                            transform: 'translateY(1px)',
                                        },
                                        '&.Mui-disabled': {
                                            background: 'rgba(255, 255, 255, 0.12)',
                                            color: 'rgba(255, 255, 255, 0.3)'
                                        }
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar Sesión'}
                                </Button>
                            </form>

                            <Box sx={{ mt: 4, textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.45)', fontFamily: "'Outfit', sans-serif" }}>
                                    ¿Problemas para acceder?{' '}
                                    <Box
                                        component="span"
                                        onClick={() => alert('Por favor, contacta al administrador del sistema para restablecer tu cuenta.')}
                                        sx={{
                                            color: '#38bdf8',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'color 0.2s',
                                            '&:hover': { 
                                                color: '#c084fc',
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        Contactar soporte
                                    </Box>
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    <Box mt={4} textAlign="center">
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.35)', fontSize: '0.8rem', fontWeight: 500, fontFamily: "'Outfit', sans-serif" }}>
                            Sistema WISP • Portal del Técnico v1.1
                        </Typography>
                    </Box>
                </Container>
            </Fade>
        </Box>
    );
};

export default TechnicianLogin;